import { Injectable, inject } from '@angular/core';
import { environment } from 'src/environments/environment';
import { StorageService } from '../storage/storage.service';
import { RRHH_INDEX_HINTS } from '../../constants/rrhh-employee-profiles';

export interface AmaiaUser {
  name: string;
  nickname: string;
  email: string;
  picture: string;
  index_profile: string[];
}

const REQUIRED_SUPER_USER_INDEXES = [
  'amr_index',
  'atc_index',
  'gpos_index',
  'gss_index',
  'in_house_index',
  'linc_index',
  'res_index'
];

/**
 * Localiza el claim `index_profile` aunque Identidades lo publique bajo una
 * clave con namespace (p. ej. "https://aeromexico.com/index_profile"), como
 * recomienda Auth0 para custom claims.
 */
function extractRawIndexProfile(user: Record<string, unknown>): unknown {
  if (user['index_profile'] !== undefined) return user['index_profile'];
  const namespacedKey = Object.keys(user).find((key) =>
    key.toLowerCase().endsWith('index_profile')
  );
  return namespacedKey ? user[namespacedKey] : undefined;
}

/**
 * Normaliza el claim a `string[]` tolerando los formatos que puede emitir la
 * Action de Auth0: array de strings, string JSON ("[\"res_index\"]"), string
 * separado por comas/espacios ("res_index,amr_index") o un único valor.
 * Antes, cualquier formato no-array se descartaba como `[]` y el usuario era
 * rechazado con "No cuentas con área comercial o rol asignado".
 */
export function normalizeIndexProfile(raw: unknown): string[] {
  let value: unknown = raw;
  if (typeof value === 'string') {
    const text = value.trim();
    if (text.startsWith('[')) {
      try {
        value = JSON.parse(text);
      } catch {
        value = text;
      }
    }
    if (typeof value === 'string') {
      value = value.split(/[,;\s]+/);
    }
  }
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => String(item).trim().toLowerCase())
    .filter((item) => item.length > 0);
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  readonly #storage = inject(StorageService);

  public getUser(): AmaiaUser {
    const user = this.#storage.get('user') || {};
    return {
      name: user.name ?? '',
      nickname: user.nickname ?? '',
      email: user.email ?? '',
      picture: user.picture ?? '',
      index_profile: normalizeIndexProfile(extractRawIndexProfile(user))
    };
  }

  public getEmployeeId(): string {
    return this.getUser().email;
  }

  public getIndexProfile(): string[] {
    return this.getUser().index_profile;
  }

  public isSuperUser(): boolean {
    const indexes = new Set(this.getIndexProfile());
    return REQUIRED_SUPER_USER_INDEXES.every((index) => indexes.has(index));
  }

  /**
   * Agentes especializados RRHH/LINC (~34): su claim Auth0 `index_profile`
   * contiene exclusivamente índices RRHH (linc/mla). Un super usuario tiene
   * todos los índices, por lo que queda fuera. El combo además está tras el
   * flag de entorno `rrhhEmployeeProfileCombo`.
   */
  public isRrhhSpecialized(): boolean {
    if (!environment.rrhhEmployeeProfileCombo) return false;
    const indexes = this.getIndexProfile();
    if (!indexes.length) return false;
    const rrhhIndexes: readonly string[] = RRHH_INDEX_HINTS;
    return indexes.every((index) => rrhhIndexes.includes(index));
  }
}
